'use client';

import { Fragment, useState } from 'react';
import { Android, Apple, Desktop, ExtensionBase, Mail, Watch } from '@doist/icons/brand';
import { Box, Container, Hidden, Image } from '@doist/marketist';
import { useTranslation } from 'app/i18n/client';
import cn from 'clsx';
import { FeatureRadioTile } from 'components/feature-radio-tile/feature-radio-tile';
import { FeatureRadioTileGroup } from 'components/feature-radio-tile/feature-radio-tile-group';
import { IconRadioTile } from 'components/icon-radio-tile/icon-radio-tile';
import { IconRadioTileGroup } from 'components/icon-radio-tile/icon-radio-tile-group';
import { ProductUIFrameBackground } from 'components/product-ui/product-ui-frame-background/product-ui-frame-background';
import { ProductUIThemeProvider } from 'components/product-ui/product-ui-theme-context';
import { useLocale } from 'next-intl';
import { createValueTypeGuard } from 'services/create-value-type-guard';
import css from './apps-section.module.css';
import type { CSSProperties, ReactElement } from 'react';
import { WeeklyCalendarViewScene } from '@/components/product-ui/scenes/weekly-calendar-view';
const allTabs = ['desktop', 'desktop-calendar', 'android', 'ios', 'wearables', 'browser-extensions', 'email-add-ons'] as const;
type Tab = (typeof allTabs)[number];
const defaultTabs: Tab[] = ['desktop', 'android', 'ios', 'wearables', 'browser-extensions', 'email-add-ons'];
const typeGuard = createValueTypeGuard<Tab>(allTabs);
type LocalisedImages = {
  id: Tab;
  src: string | ReactElement;
  label: string;
  icon: ReactElement;
};
function localisedImages(lang: string): LocalisedImages[] {
  return [{
    id: 'desktop',
    src: `/static/apps-section/${lang}/desktop.png`,
    label: 'desktop',
    icon: <Desktop />
  }, {
    id: 'desktop-calendar',
    src: <WeeklyCalendarViewScene />,
    label: 'desktop',
    icon: <Desktop />
  }, {
    id: 'android',
    src: `/static/apps-section/${lang}/android.png`,
    label: 'android',
    icon: <Android />
  }, {
    id: 'ios',
    src: `/static/apps-section/${lang}/ios.png`,
    label: 'ios',
    icon: <Apple />
  }, {
    id: 'wearables',
    src: `/static/apps-section/${lang}/wearables.png`,
    label: 'wearables',
    icon: <Watch />
  }, {
    id: 'browser-extensions',
    src: `/static/apps-section/${lang}/browser-extensions.png`,
    label: 'browserExtensions',
    icon: <ExtensionBase />
  }, {
    id: 'email-add-ons',
    src: `/static/apps-section/${lang}/email-add-ons.png`,
    label: 'emailAddOns',
    icon: <Mail />
  }];
}
type AppsSectionProps = {
  colorTheme?: 'green' | 'red' | 'seaSalt' | 'yellow';
  tabs?: Tab[];
  style?: CSSProperties;
  initialTab?: Tab;
};
function AppsSection({
  colorTheme = 'yellow',
  tabs = defaultTabs,
  style,
  initialTab
}: AppsSectionProps) {
  const locale = useLocale();
  const {
    t,
    i18n
  } = useTranslation(locale, 'features-teams');
  const images = localisedImages(i18n.language).filter(({
    id
  }) => tabs?.includes(id));
  const [iconGroup, setIconGroup] = useState<Tab>(initialTab ?? images[0].id);
  function iconRadioGroupChangeHandler(value: string) {
    if (typeGuard(value)) {
      setIconGroup(value);
    }
  }
  return <ProductUIThemeProvider theme={colorTheme} data-sentry-element="ProductUIThemeProvider" data-sentry-component="AppsSection" data-sentry-source-file="apps-section.tsx">
            <Hidden above="md" data-sentry-element="Hidden" data-sentry-source-file="apps-section.tsx">
                <Container width="base" style={style} padded={false} data-sentry-element="Container" data-sentry-source-file="apps-section.tsx">
                    <FeatureRadioTileGroup onChange={iconRadioGroupChangeHandler} value={iconGroup} data-sentry-element="FeatureRadioTileGroup" data-sentry-source-file="apps-section.tsx">
                        {images.map(({
            id,
            icon,
            label,
            src
          }) => <FeatureRadioTile key={id} title={t(label)} icon={icon} value={id} scene={typeof src === 'string' ? <Image key={`${id}-scene`} src={`${process.env.NEXT_PUBLIC_BASE_URL}${src}`} className={cn(css.image, {
            [css.active]: iconGroup === id
          })} width={1088} height={544} alt="" /> : <Fragment key={`${id}-scene`}>{src}</Fragment>} />)}
                    </FeatureRadioTileGroup>
                </Container>
            </Hidden>

            <Hidden below="md" data-sentry-element="Hidden" data-sentry-source-file="apps-section.tsx">
                <Container width="base" className={css.galleryContainer} style={style} padded={false} data-sentry-element="Container" data-sentry-source-file="apps-section.tsx">
                    <Box className={css.controlContainer} data-sentry-element="Box" data-sentry-source-file="apps-section.tsx">
                        <IconRadioTileGroup value={iconGroup} onChange={iconRadioGroupChangeHandler} data-sentry-element="IconRadioTileGroup" data-sentry-source-file="apps-section.tsx">
                            {images.map(({
              id,
              icon,
              label
            }) => <IconRadioTile key={id} icon={icon} value={id}>
                                    {t(label)}
                                </IconRadioTile>)}
                        </IconRadioTileGroup>
                    </Box>
                    <Box className={cn(css.imageContainer)} data-sentry-element="Box" data-sentry-source-file="apps-section.tsx">
                        <ProductUIFrameBackground className={css.background} variant="wave-two" data-sentry-element="ProductUIFrameBackground" data-sentry-source-file="apps-section.tsx" />
                        {images.map(({
            id,
            src
          }) => typeof src === 'string' ? <Image key={id} src={`${process.env.NEXT_PUBLIC_BASE_URL}${src}`} className={cn(css.image, {
            [css.active]: iconGroup === id
          })} width={1088} height={544} alt="" /> : <Box className={cn(css.image, {
            [css.active]: iconGroup === id
          })} key={`${id}-scene`}>
                                    {src}
                                </Box>)}
                    </Box>
                </Container>
            </Hidden>
        </ProductUIThemeProvider>;
}
export { AppsSection };
export type { Tab };